import domready from "domready"
import Modal from "react-modal"
import "./style.css"
import { allPalettesWithBlack } from "./randomPalette"
import spectral from "spectral.js"
import { voronoi } from "d3-voronoi"
import { polygonCentroid } from "d3-polygon"
import React from "react"
import { createRoot } from "react-dom/client"
import Config from "./ui/Config"
import { rndFromArray } from "./util"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import RenderingStrategy from "./Strategy"
import { registerValidator } from "./form/useForm"
import { registerValidators } from "./registerValidators"
import { registerRenderers } from "./registerRenderers"
import Color from "./Color"


const PHI = (1 + Math.sqrt(5)) / 2;
const TAU = Math.PI * 2;
const DEG2RAD_FACTOR = TAU / 360;

const config = {
    width: 0,
    height: 0,
    debug: false
};

/**
 * @type CanvasRenderingContext2D
 */
let ctx;
let canvas;


function relax(v, pts, count = 5)
{
    for (let i = 0; i < count; i++)
    {
        pts = v(pts).polygons().map(poly => {
            const c = polygonCentroid(poly)
            c[0] |= 0
            c[1] |= 0
            return c
        })
    }
    return pts
}

function drawPolygon(ctx, polygon)
{
    const last = polygon.length - 1
    const [x, y] = polygon[last]

    ctx.beginPath()
    ctx.moveTo(
        Math.round(x),
        Math.round(y)
    )

    for (let i = 0; i < polygon.length; i++)
    {
        const [x, y] = polygon[i]
        ctx.lineTo(
            Math.round(x),
            Math.round(y)
        )
    }
    ctx.fill()
    ctx.stroke()
}

const overdraw = 1.15

let counter = 0
let palette
let useSpectralJs = true

class ColorPlot
{
    pts = []
    colors = []

    plot(color,x0,y0)
    {
        this.colors.push(color)
        this.pts.push([x0|0,y0|0])
    }

    fill(debug)
    {
        const { pts, colors } = this
        const { width, height } = config

        const borderX = (overdraw - 1) * width / 2
        const borderY = (overdraw - 1) * height / 2
        const v = voronoi().extent([[-borderX,-borderY], [width + borderX, height + borderY]])

        if (debug)
        {

            ctx.fillStyle = "#000"
            ctx.fillRect(0,0,width,height)

            for (let i = 0; i < pts.length; i++)
            {
                const [x, y] = pts[i]
                ctx.fillStyle = colors[i]
                ctx.fillRect(x - 1, y - 1, 2, 2)
            }
        }
        else
        {
            const diagram = v(pts)
            diagram.polygons().forEach(
                (p, idx) => {
                    ctx.fillStyle = colors[idx]
                    ctx.strokeStyle = colors[idx]
                    ctx.lineWidth = 2
                    drawPolygon(ctx, p)
                })
        }
    }
}





const pairedRanges = (palette, colorPlot, cfg) => {

    const { width, height } = config

    const cx = width >> 1
    const cy = height >> 1

    const radius = Math.floor(Math.min(cx,cy) * 0.98) // >> 1

    const { debug, steps } = cfg;
    ctx.save()

    //console.log("PALETTE", palette.length)

    const pLen = palette.length

    const halfPaletteLen = pLen >> 1
    const halfCount = steps >> 1
    const halfAxes = new Array(halfPaletteLen)

    for (let i = 0; i < halfPaletteLen; i++)
    {
        const color = palette[i]
        const other = palette[(i + halfPaletteLen) % pLen]
        const spread = createPalette(color, other, steps)
        halfAxes[i] = spread.slice(0, halfCount).reverse()
        halfAxes[i + halfPaletteLen] = spread.slice(halfCount)
    }

    const drawAxis = (colorPlot, spokes, index, cx, cy, radius, steps) => {
        const hLen = spokes.length

        const next = (index + 1) % hLen
        const spoke = spokes[index]
        const nextSpoke = spokes[next]

        const angleStep = TAU/hLen

        const rStep = radius * 2 / (steps - 1)
        for (let j = 0; j < spoke.length; j++)
        {
            const color = spoke[j]

            const angle = TAU/4 + index * angleStep
            const r = (rStep >> 1) + j * rStep
            let x0 = cx + Math.cos(angle) * r
            let y0 = cy + Math.sin(angle) * r
            let x1 = cx + Math.cos(angle + angleStep) * r
            let y1 = cy + Math.sin(angle + angleStep) * r
            colorPlot.plot(color, x0, y0)

            if (j > 0)
            {
                const other = nextSpoke[j]
                const subLen = 2 + j
                const sub = createPalette(color, other, subLen)

                let dt = 1 / (subLen - 1)
                let t = dt
                for (let k = 1; k < subLen - 1; k++)
                {
                    const x2 = x0 + (x1 - x0) * t
                    const y2 = y0 + (y1 - y0) * t

                    colorPlot.plot(sub[k], x2, y2)
                    t += dt
                }
            }
        }
    }

    for (let i = 0; i < halfAxes.length; i++)
    {
        drawAxis(colorPlot, halfAxes, i, cx, cy, radius, steps)
    }

    //pts = relax(v, pts, 0)
    colorPlot.fill(debug)

    ctx.restore()
}

const tmp = new Color(0,0,0)

function createPalette(colorA, colorB, steps)
{

    if (useSpectralJs)
    {
        return spectral.palette(colorA, colorB, steps, spectral.HEX)
    }
    else
    {
        colorA = Color.from(colorA)
        colorB = Color.from(colorB)

        let palette = []

        let t = 0
        const dt = 1/(steps-1)
        for (let i = 0; i < steps; i++)
        {
            colorA.mix(colorB, t, tmp)
            palette.push(tmp.toRGBHex())
            t += dt
        }

        console.log("createPalette", colorA, colorB, steps, "->", palette.length)

        return palette
    }
}



const radiating = (palette, colorPlot, cfg) => {

    const { width, height } = config

    const cx = width >> 1
    const cy = height >> 1

    const radius = Math.floor(Math.min(cx,cy) * 0.98) // >> 1

    const { debug, steps, center } = cfg;
    ctx.save()

    //console.log("PALETTE", palette.length)

    const pLen = palette.length

    const spokesLen = pLen - 1
    const spokes = []

    const color = palette[center]
    for (let i = 0; i < pLen; i++)
    {
        if ( i !== center)
        {
            const other = palette[i]
            spokes.push(
                createPalette(color, other, steps)
            )
        }
    }

    colorPlot.plot(color,cx,cy)

    const drawAxis = (colorPlot, spokes, index, cx, cy, radius, steps) => {
        const hLen = spokes.length

        const next = (index + 1) % hLen
        const spoke = spokes[index]
        const nextSpoke = spokes[next]

        const angleStep = TAU/hLen

        const rStep = radius / (steps - 1)
        for (let j = 1; j < spoke.length; j++)
        {
            const color = spoke[j]

            const angle = TAU/4 + index * angleStep
            const r = j * rStep
            let x0 = cx + Math.cos(angle) * r
            let y0 = cy + Math.sin(angle) * r
            let x1 = cx + Math.cos(angle + angleStep) * r
            let y1 = cy + Math.sin(angle + angleStep) * r
            colorPlot.plot(color, x0, y0)

            if (j > 0)
            {
                const other = nextSpoke[j]
                const subLen = 2 + j
                const sub = createPalette(color, other, subLen)

                let dt = 1 / (subLen - 1)
                let t = dt
                for (let k = 1; k < subLen - 1; k++)
                {
                    const x2 = x0 + (x1 - x0) * t
                    const y2 = y0 + (y1 - y0) * t

                    colorPlot.plot(sub[k], x2, y2)
                    t += dt
                }
            }
        }
    }

    for (let i = 0; i < spokes.length; i++)
    {
        drawAxis(colorPlot, spokes, i, cx, cy, radius, steps)
    }

    //pts = relax(v, pts, 0)
    colorPlot.fill(debug)

    ctx.restore()
}

const dummy = (palette) => {

    const { width, height} = config
    ctx.fillStyle = "#000"
    ctx.fillRect(0,0,width, height)
}
const strategies = [
    new RenderingStrategy(
        "Radiating",
        radiating,
        {
            debug: false,
            steps: 12,
            center: 0
        }
    ),
    new RenderingStrategy(
        "PairedRanges",
        pairedRanges,
        {
            debug: false,
            steps: 24
        }
    ),
]
let strategy = strategies[0]
let strategyConfig = strategy.defaultConfig


function paintPalette()
{
    strategy.fn(palette, new ColorPlot(), strategyConfig)
}


domready(
    () => {

        canvas = document.getElementById("screen");
        ctx = canvas.getContext("2d");

        const width = (window.innerWidth) | 0;
        const height = (window.innerHeight) | 0;

        config.width = width;
        config.height = height;

        canvas.width = width;
        canvas.height = height;

        palette = rndFromArray(allPalettesWithBlack)
        paintPalette()

        const domNode = document.getElementById('react-root');
        const root = createRoot(domNode);

        Modal.setAppElement(domNode)


        function render()
        {
            root.render(
                <>
                    <Config
                        useSpectralJs={ useSpectralJs }
                        setUseSpectralJs={ f => {
                            useSpectralJs = f
                            paintPalette()
                            render()
                        }}
                        strategyConfig={ strategyConfig }
                        setStrategyConfig={ s => {
                            strategyConfig = s;
                            paintPalette()
                            render()
                        }}
                        palette={ palette }
                        setPalette={
                            p => {
                                palette = p
                                paintPalette()
                                render()
                            }
                        }
                        strategy={ strategy }
                        setStrategy={ s => {
                            strategy = s
                            strategyConfig = s.defaultConfig
                            paintPalette()
                            render()
                        } }
                        strategies={ strategies }
                    />
                    <ToastContainer position="top-center"/>
                </>
            )

        }

        registerRenderers(() => palette)
        registerValidators()
        render()

        canvas.addEventListener("click",  () => {
            palette = rndFromArray(allPalettesWithBlack)
            paintPalette()
            render()
        }, true)
    }
);
